import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { ProgressButton, useToast } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { FcGridInfo } from "~/components/serviceSupport/FcGridInfo";
import { SettingsToggle } from "~/components/serviceSupport/SettingsToggle";
import {
  DangerZoneWrapper,
  SettingsDangerZoneRow
} from "~/components/settings/DangerZoneWrapper";
import { SettingsSectionWrapper } from "~/components/settings/SettingsSection";
import envConstants from "~/config/envConstants";
import useFlag from "~/config/flags";

import {
  DESIGN_PREVIEW_MODAL_NAV_OPTIONS,
  DEV_TOOLS_ICON_LIST_PATH,
  SETTINGS_PREVIEW_UPDATE_CONFIGURATION_PATH
} from "~/features/preview/paths";
import {
  settingsPagePaddingSx,
  settingsSectionGap,
  settingsWideButtonWidthPx
} from "~/features/settings/settings.styles";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { clearFulfillmentCenter } from "~/redux/actions/store";
import { setSiteFcId, setThisWorkstation } from "~/redux/actions/workstations";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectLanguageCode } from "~/redux/selectors/siteSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useLazyGetWorkstationsQuery,
  usePostDeactivateWorkstationMutation
} from "~/redux/warehouse/workstation.hooks";

import { toggleConveyanceSimEnabled } from "./conveyanceSim";

import {
  selectEnableDevInformation,
  selectPtlSimulationEnabled,
  toggleDevInfomation,
  togglePtlSimulationEnabled
} from "./devTools.slice";

const CLEAR_FC_CONFIRMATION_PHRASE = "CLEAR";
const DEACTIVATE_ALL_CONFIRMATION_PHRASE = "DEACTIVATE";
const UPDATE_CONFIGURATION_CONFIRMATION_PHRASE = "UPDATE";

export function DevToolsView() {
  const ptlSimulationEnabled = useAppSelector(selectPtlSimulationEnabled);
  const conveyanceSimEnabled = useAppSelector(
    (state) => state.conveyance.conveyanceSimEnabled
  );
  const loginPath = useAppSelector(
    (state) => state?.login?.profile?.login_path
  );
  const workstation = useAppSelector(selectThisWorkstation);

  const { t, i18n } = useTranslation();
  const navigate = useFcNavigate();
  const navigateWithoutFcQuery = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const overviewAccentValueSx = {
    color: `var(--text-accent-primary, ${theme.palette.primary.main})`,
    fontFamily: "CircularXX, sans-serif",
    fontWeight: 400,
    textDecoration: "none"
  } as const;
  const { errorToast, successToast } = useToast();
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const devInformationEnabled = useAppSelector(selectEnableDevInformation);
  const languageCode = useAppSelector(selectLanguageCode);
  const userFc = useAppSelector((state) => state.store.usersFulfillmentCenter);

  const { dev_ptlSimulation, dev_conveyanceSimulation } =
    useAppSelector(selectServerConfig);

  const areDevCheatsEnabled =
    envConstants.DEV_CHEATS_UAT === "true" ||
    envConstants.DEV_CHEATS_STAGING === "true";

  const [clearFcConfirmation, setClearFcConfirmation] = useState("");
  const [deactivateAllConfirmation, setDeactivateAllConfirmation] =
    useState("");
  const [updateConfigurationConfirmation, setUpdateConfigurationConfirmation] =
    useState("");
  const [isDeactivatingAllWorkstations, setIsDeactivatingAllWorkstations] =
    useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [fetchWorkstations] = useLazyGetWorkstationsQuery();
  const [postDeactivateWorkstation] = usePostDeactivateWorkstationMutation();

  const { enableSideNavSubMenus } = useFlag();

  const canConfirmClearFc =
    clearFcConfirmation === CLEAR_FC_CONFIRMATION_PHRASE;

  const canConfirmDeactivateAllWorkstations =
    deactivateAllConfirmation === DEACTIVATE_ALL_CONFIRMATION_PHRASE &&
    !!userFc?.fulfillmentCenterId;

  const canConfirmUpdateConfiguration =
    updateConfigurationConfirmation ===
    UPDATE_CONFIGURATION_CONFIRMATION_PHRASE;

  const handleClearFc = () => {
    if (!canConfirmClearFc) {
      return;
    }
    dispatch(clearFulfillmentCenter());
    dispatch(setSiteFcId(null, null));
    setClearFcConfirmation("");
    navigateWithoutFcQuery(
      enableSideNavSubMenus ? "/settings/general" : "/settings"
    );
  };

  const handleDeactivateAllWorkstations = async () => {
    if (!canConfirmDeactivateAllWorkstations) {
      return;
    }
    setIsDeactivatingAllWorkstations(true);
    try {
      const workstations = await fetchWorkstations().unwrap();
      if (workstations.length === 0) {
        successToast(t("no workstations to deactivate"));
        await dispatch(setThisWorkstation(null, false));
        setDeactivateAllConfirmation("");
        void fetchWorkstations();
        return;
      }

      const results = await Promise.allSettled(
        workstations.map((w) => postDeactivateWorkstation(w.id).unwrap())
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        errorToast(t("deactivate all workstations partial failure"));
      } else {
        successToast(t("all workstations deactivated"));
      }
      await dispatch(setThisWorkstation(null, false));
      setDeactivateAllConfirmation("");
      void fetchWorkstations();
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    } finally {
      setIsDeactivatingAllWorkstations(false);
    }
  };

  const handleOpenUpdateConfiguration = () => {
    if (!canConfirmUpdateConfiguration) {
      return;
    }
    navigate(SETTINGS_PREVIEW_UPDATE_CONFIGURATION_PATH);
    setUpdateConfigurationConfirmation("");
  };

  const openPreviewModal = () => {
    setPreviewModalOpen(true);
  };

  const navigateToPreviewPath = (path: string) => {
    const allowed = DESIGN_PREVIEW_MODAL_NAV_OPTIONS.some(
      (o) => o.path === path
    );
    if (!allowed) {
      return;
    }
    navigate(path);
    setPreviewModalOpen(false);
  };

  const hiddenBtnSx = {
    width: settingsWideButtonWidthPx,
    maxWidth: "100%"
  } as const;

  return (
    <Box sx={settingsPagePaddingSx}>
      <Stack spacing={settingsSectionGap} sx={{ width: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap={{ xs: "wrap", md: "nowrap" }}
          gap={1.5}
          sx={{ minWidth: 0, columnGap: 2 }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              flex: "0 1 auto",
              minWidth: 0,
              fontWeight: 500,
              letterSpacing: 0
            }}
          >
            {t("nav.viewname.dev tools")}
          </Typography>
          <Box
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" }
            }}
          >
            <FcGridInfo />
          </Box>
        </Stack>

        <SettingsSectionWrapper title={t("overview")}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, sm: 3 }}
            alignItems={{ xs: "stretch", sm: "flex-start" }}
            sx={{ width: "100%" }}
          >
            <Stack
              spacing={2}
              sx={{ width: { xs: "100%", sm: "50%" }, minWidth: 0 }}
            >
              <Stack
                direction="row"
                alignItems="baseline"
                gap={1}
                flexWrap="wrap"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  minWidth={200}
                >
                  {t("fulfillment center")}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={overviewAccentValueSx}
                >
                  {userFc?.name ?? "—"}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="baseline"
                gap={1}
                flexWrap="wrap"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  minWidth={200}
                >
                  {t("assigned workstation")}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={overviewAccentValueSx}
                >
                  {workstation?.deviceId ?? "—"}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              alignItems="baseline"
              gap={1}
              flexWrap="wrap"
              sx={{
                width: { xs: "100%", sm: "50%" },
                minWidth: 0,
                justifyContent: "flex-start"
              }}
            >
              <Typography variant="body2" color="text.secondary" minWidth={100}>
                {t("login path")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {loginPath ?? "—"}
              </Typography>
            </Stack>
          </Stack>
        </SettingsSectionWrapper>

        <Divider />

        <SettingsSectionWrapper title={t("utilities")}>
          <Stack spacing={2} alignItems="flex-start">
            {areDevCheatsEnabled && isAdmin && (
              <SettingsToggle
                labelId="dev-info-enabled"
                label={t("dev information")}
                infoTooltip="Toggles the display of configuration tab in the service support view"
                checked={devInformationEnabled}
                onChange={(c) => dispatch(toggleDevInfomation(c))}
              />
            )}
            <SettingsToggle
              labelId="translation-keys"
              label={t("translation keys")}
              infoTooltip="Turns translations off and displays text as untranslated key"
              checked={i18n.language === "cimode"}
              onChange={() => {
                const newLanguage =
                  i18n.language !== "cimode" ? "cimode" : languageCode;
                void i18n.changeLanguage(newLanguage);
              }}
            />
            {(envConstants.ENABLE_PTL_SIMULATION === "true" ||
              dev_ptlSimulation) && (
              <SettingsToggle
                labelId="ptl-simulation-enabled"
                label={t("ptl simulation")}
                infoTooltip="Displays PTL simulation component on picking page"
                checked={ptlSimulationEnabled}
                onChange={() => dispatch(togglePtlSimulationEnabled())}
              />
            )}
            {(envConstants.ENABLE_CONVEYANCE_SIMULATION === "true" ||
              dev_conveyanceSimulation) && (
              <SettingsToggle
                labelId="conveyance-simulation-enabled"
                label={t("conveyance simulation")}
                infoTooltip="Simulates conveyer functionality on original bin maintenance/cleaning page"
                checked={conveyanceSimEnabled}
                onChange={() => dispatch(toggleConveyanceSimEnabled())}
              />
            )}
          </Stack>
        </SettingsSectionWrapper>

        <Divider />

        <SettingsSectionWrapper title={t("hidden features")}>
          <Stack spacing={1.5} alignItems="flex-start">
            <ProgressButton
              size="large"
              variant="subtle"
              onClick={() => navigate("/order-creation")}
              sx={hiddenBtnSx}
            >
              {t("order creation")}
            </ProgressButton>
            <ProgressButton
              variant="subtle"
              size="large"
              onClick={() =>
                navigate("/workstation-bin-maintenance?autostore=true")
              }
              sx={hiddenBtnSx}
            >
              {t("workstation maintenance")}
            </ProgressButton>
            <ProgressButton
              variant="subtle"
              size="large"
              onClick={() => navigate("/escalated-bins?autostore=true")}
              sx={hiddenBtnSx}
            >
              {t("nav.viewname.escalated bins")}
            </ProgressButton>
            <ProgressButton
              size="large"
              variant="subtle"
              onClick={openPreviewModal}
              sx={hiddenBtnSx}
            >
              {t("design previews")}
            </ProgressButton>
            <ProgressButton
              variant="subtle"
              size="large"
              onClick={() => navigate(DEV_TOOLS_ICON_LIST_PATH)}
              sx={hiddenBtnSx}
            >
              {t("nav.viewname.settings icon table")}
            </ProgressButton>
          </Stack>
        </SettingsSectionWrapper>

        <Divider />

        <DangerZoneWrapper title={t("danger zone")}>
          <SettingsDangerZoneRow
            title={t("clear fc selection")}
            hint={t("clear fc selection redirect hint")}
            confirmationPhrase={CLEAR_FC_CONFIRMATION_PHRASE}
            confirmationValue={clearFcConfirmation}
            onConfirmationChange={setClearFcConfirmation}
            actionLabel={t("clear fc selection")}
            onAction={handleClearFc}
            actionDisabled={!canConfirmClearFc}
          />
          <SettingsDangerZoneRow
            title={t("deactivate all workstations")}
            hint={
              userFc?.fulfillmentCenterId
                ? t("deactivate all workstations hint")
                : t("deactivate all workstations select fc first")
            }
            confirmationPhrase={DEACTIVATE_ALL_CONFIRMATION_PHRASE}
            confirmationValue={deactivateAllConfirmation}
            onConfirmationChange={setDeactivateAllConfirmation}
            actionLabel={t("deactivate all workstations")}
            onAction={() => void handleDeactivateAllWorkstations()}
            fieldDisabled={!userFc?.fulfillmentCenterId}
            actionDisabled={
              !canConfirmDeactivateAllWorkstations ||
              isDeactivatingAllWorkstations
            }
          />
          <SettingsDangerZoneRow
            title={t("update client configuration")}
            hint={t("open client configuration hint")}
            confirmationPhrase={UPDATE_CONFIGURATION_CONFIRMATION_PHRASE}
            confirmationValue={updateConfigurationConfirmation}
            onConfirmationChange={setUpdateConfigurationConfirmation}
            actionLabel={t("open update configuration")}
            onAction={handleOpenUpdateConfiguration}
            actionDisabled={!canConfirmUpdateConfiguration}
          />
        </DangerZoneWrapper>
      </Stack>
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        aria-labelledby="dev-tools-layout-dialog-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="dev-tools-layout-dialog-title">
          {t("design previews")}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <List disablePadding dense>
            {DESIGN_PREVIEW_MODAL_NAV_OPTIONS.map((opt) => (
              <ListItemButton
                key={opt.path}
                onClick={() => navigateToPreviewPath(opt.path)}
              >
                <ListItemText primary={t(opt.labelKey)} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewModalOpen(false)}>
            {t("cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
